import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  DndContext, PointerSensor, useSensor, useSensors, useDraggable, useDroppable, DragOverlay
} from "@dnd-kit/core";
import { Loader2, MoreVertical, RefreshCw, User, MapPin, Calendar } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];

const COLUMNS = [
  { key: "pending_payment",  label: "Pending Payment",  accent: "#F59E0B" },
  { key: "paid",             label: "Paid",             accent: "#C5DB7B" },
  { key: "accepted",         label: "Accepted",         accent: "#60A5FA" },
  { key: "in_progress",      label: "Crafting",         accent: "#D4859B" },
  { key: "packed",           label: "Packed",           accent: "#D4859B" },
  { key: "out_for_delivery", label: "Out for Delivery", accent: "#C5DB7B" },
  { key: "delivered",        label: "Delivered",        accent: "#22C55E" },
  { key: "completed",        label: "Completed",        accent: "#94A3B8" },
  { key: "cancelled",        label: "Cancelled",        accent: "#EF4444" }
];

export default function AdminOrdersBoard() {
  const [ordersByStatus, setOrdersByStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeOrder, setActiveOrder] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/orders?limit=200`, { withCredentials: true });
      const map = {};
      COLUMNS.forEach(c => (map[c.key] = []));
      (r.data.orders || []).forEach(o => {
        if (map[o.status]) map[o.status].push(o);
        else map["pending_payment"].push(o); // unknown bucket fallback
      });
      setOrdersByStatus(map);
    } catch {
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDragStart = (e) => {
    const id = e.active.id;
    let found = null;
    for (const arr of Object.values(ordersByStatus)) {
      const o = arr.find(o => o.order_id === id);
      if (o) { found = o; break; }
    }
    setActiveOrder(found);
  };

  const handleDragEnd = async (e) => {
    setActiveOrder(null);
    const { active, over } = e;
    if (!over) return;
    const orderId = active.id;
    const newStatus = over.id;

    // Find current status
    let currentStatus = null;
    let order = null;
    for (const [status, arr] of Object.entries(ordersByStatus)) {
      const o = arr.find(o => o.order_id === orderId);
      if (o) { currentStatus = status; order = o; break; }
    }
    if (!order || currentStatus === newStatus) return;

    // Optimistic update
    setOrdersByStatus(prev => {
      const next = { ...prev };
      next[currentStatus] = next[currentStatus].filter(o => o.order_id !== orderId);
      next[newStatus] = [{ ...order, status: newStatus }, ...next[newStatus]];
      return next;
    });
    try {
      await axios.put(`${API}/admin/orders/${orderId}/status`, { status: newStatus, note: "Updated via Kanban board" }, { withCredentials: true });
      toast.success(`Order → ${COLUMNS.find(c => c.key === newStatus)?.label}`);
    } catch {
      toast.error("Failed to update status");
      load(); // rollback by reloading
    }
  };

  return (
    <div data-testid="admin-orders-board">
      <header className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <span className="w-8 h-px bg-seikat-pink" />
            <span className="eyebrow-immersive">Orders</span>
          </div>
          <h1 className="font-heading text-4xl lg:text-5xl text-seikat-bone leading-tight">
            <em className="text-seikat-pink">Kanban</em> Pesanan.
          </h1>
          <p className="font-body text-seikat-bone/65 mt-3 max-w-2xl">
            Tarik kartu antar kolom untuk memperbarui status. Setiap perpindahan akan memicu email notifikasi ke pelanggan.
          </p>
        </div>
        <button onClick={load} className="btn-pill text-[10px]" data-testid="board-refresh">
          <RefreshCw size={12} /> Refresh
        </button>
      </header>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 size={24} className="animate-spin text-seikat-pink" /></div>
      ) : (
        <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          <div className="flex gap-4 overflow-x-auto pb-6 -mx-6 lg:-mx-12 px-6 lg:px-12 snap-x">
            {COLUMNS.map(col => (
              <Column key={col.key} column={col} orders={ordersByStatus[col.key] || []} />
            ))}
          </div>
          <DragOverlay>
            {activeOrder ? <OrderCard order={activeOrder} dragging /> : null}
          </DragOverlay>
        </DndContext>
      )}
    </div>
  );
}

function Column({ column, orders }) {
  const { setNodeRef, isOver } = useDroppable({ id: column.key });
  return (
    <div
      ref={setNodeRef}
      className={`shrink-0 snap-start w-[280px] rounded-2xl border ${isOver ? "border-seikat-pink bg-seikat-pink/8" : "border-seikat-pink/12 bg-white/55"} backdrop-blur-md p-4 flex flex-col max-h-[78vh]`}
      data-testid={`kanban-column-${column.key}`}
    >
      <header className="flex items-center justify-between mb-4 px-1">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: column.accent }} />
          <h3 className="font-heading text-base text-seikat-bone">{column.label}</h3>
        </div>
        <span className="text-[10px] tracking-widest uppercase text-seikat-bone-muted px-2 py-0.5 rounded-full bg-white/60">
          {orders.length}
        </span>
      </header>
      <div className="flex-1 overflow-y-auto space-y-2 pr-1">
        <AnimatePresence>
          {orders.length === 0 ? (
            <div className="h-24 flex items-center justify-center text-[10px] tracking-widest uppercase text-seikat-bone-muted/60 border-2 border-dashed border-seikat-bone-muted/15 rounded-xl">
              Drop here
            </div>
          ) : (
            orders.map(o => <DraggableOrderCard key={o.order_id} order={o} />)
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

function DraggableOrderCard({ order }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: order.order_id });
  return (
    <motion.div
      layout
      ref={setNodeRef}
      style={{ opacity: isDragging ? 0.4 : 1 }}
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: isDragging ? 0.4 : 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25, ease: EASE }}
      data-testid={`order-card-${order.order_id}`}
      {...attributes}
      {...listeners}
    >
      <OrderCard order={order} />
    </motion.div>
  );
}

function OrderCard({ order, dragging }) {
  const fmt = (p) => new Intl.NumberFormat("id-ID").format(p || 0);
  return (
    <div className={`p-3 rounded-xl bg-white/95 border border-seikat-pink/12 ${dragging ? "shadow-2xl rotate-2" : "shadow-sm"} cursor-grab active:cursor-grabbing transition-shadow hover:shadow-md`}>
      <div className="flex items-start justify-between mb-2 gap-2">
        <code className="text-[10px] font-mono text-seikat-bone-muted bg-seikat-pink/8 px-1.5 py-0.5 rounded">
          {order.order_id?.slice(0, 14)}
        </code>
        <MoreVertical size={12} className="text-seikat-bone-muted" />
      </div>
      <div className="flex items-center gap-1.5 text-xs text-seikat-bone mb-1">
        <User size={10} className="text-seikat-bone-muted" />
        <span className="truncate flex-1">{order.delivery_name || "—"}</span>
      </div>
      <div className="flex items-center gap-1.5 text-[10px] text-seikat-bone-muted mb-1">
        <MapPin size={10} />
        <span className="truncate flex-1">{order.delivery_address?.slice(0, 36) || "—"}</span>
      </div>
      {order.delivery_date && (
        <div className="flex items-center gap-1.5 text-[10px] text-seikat-bone-muted mb-2">
          <Calendar size={10} />
          <span>{order.delivery_date}</span>
        </div>
      )}
      <div className="flex items-center justify-between pt-2 border-t border-seikat-pink/10">
        <span className="font-heading text-lg text-seikat-pink leading-none">Rp {fmt(order.total)}</span>
        <span className="text-[9px] tracking-widest uppercase text-seikat-bone-muted">{(order.items || []).length} item</span>
      </div>
    </div>
  );
}
